/**
 * Wishlist System - Production Ready
 * Variant-aware, persistent, with smooth animations
 * Single Source of Truth
 */

(function () {
  'use strict';

  if (window.IlltochillWishlistInitialized) return;
  window.IlltochillWishlistInitialized = true;

  const WISHLIST_KEY = 'illtochill_wishlist';
  const EVENT_NAME = 'illtochill:wishlist-updated';

  // ============================================
  // WISHLIST DATA MANAGEMENT
  // ============================================

  function getWishlist() {
    try {
      const stored = localStorage.getItem(WISHLIST_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Error parsing wishlist:', e);
      return [];
    }
  }

  function saveWishlist(items) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { items } }));
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  function getWishlistItemKey(productId, variantId) {
    // If no variantId is provided, we might be dealing with a product-level wishlist item (fallback)
    // But ideally we always want a variantId if possible.
    return `${productId}_${variantId || 'default'}`;
  }

  function findWishlistItem(productId, variantId) {
    const wishlist = getWishlist();
    // 1. Try exact match with variant
    let key = getWishlistItemKey(productId, variantId);
    let item = wishlist.find(item => item.key === key);

    // 2. If not found and variantId is null/undefined, try finding any item with this productId
    if (!item && !variantId) {
      item = wishlist.find(item => item.productId === productId);
    }

    return item;
  }

  function addToWishlist(productData) {
    const wishlist = getWishlist();
    const key = getWishlistItemKey(productData.productId, productData.variantId);

    // Check for duplicates by key
    const existingIndex = wishlist.findIndex(item => item.key === key);
    if (existingIndex !== -1) {
      return false; // Already exists
    }

    wishlist.push({
      key: key,
      productId: productData.productId,
      variantId: productData.variantId || null,
      handle: productData.handle,
      title: productData.title,
      price: productData.price,
      image: productData.image,
      url: productData.url,
      color: productData.color || null,
      size: productData.size || null,
      addedAt: Date.now()
    });

    saveWishlist(wishlist);
    return true;
  }

  function removeFromWishlist(productId, variantId) {
    const wishlist = getWishlist();
    // Keep it simple: remove by productId if variantId is not specified, 
    // or key if it is. Ideally we use the key.

    let filtered;
    if (variantId) {
      const key = getWishlistItemKey(productId, variantId);
      filtered = wishlist.filter(item => item.key !== key);
    } else {
      // Fallback: Remove all variants of this product? Or just check if any exists?
      // Let's safe-guard: if we only have productId, remove all entries for that product
      filtered = wishlist.filter(item => item.productId !== productId);
    }

    if (filtered.length !== wishlist.length) {
      saveWishlist(filtered);
      return true;
    }
    return false;
  }

  // Remove by direct key (useful for cart page)
  function removeWishlistItemByKey(key) {
    const wishlist = getWishlist();
    const filtered = wishlist.filter(item => item.key !== key);
    if (filtered.length !== wishlist.length) {
      saveWishlist(filtered);
      return true;
    }
    return false;
  }

  function isInWishlist(productId, variantId) {
    return findWishlistItem(productId, variantId) !== undefined;
  }

  // Check if ANY variant of a product is in wishlist (for button active state on card load)
  function isProductInWishlist(productId) {
    const wishlist = getWishlist();
    return wishlist.some(item => item.productId === productId);
  }

  // ============================================
  // WISHLIST BUTTON ANIMATIONS
  // ============================================

  function animateWishlistButton(btn, isAdding) {
    const svg = btn.querySelector('svg');
    if (!svg) return;

    btn.classList.add('wishlist-btn--animating');

    if (isAdding) {
      // Scale up → fill → scale back
      btn.style.transform = 'scale(1.2)';
      setTimeout(() => {
        btn.classList.add('product-card-new__wishlist-btn--active');
        svg.style.fill = '#B90404';
        svg.style.stroke = '#B90404';
        btn.style.transform = 'scale(1)';
        setTimeout(() => {
          btn.classList.remove('wishlist-btn--animating');
        }, 200);
      }, 150);
    } else {
      // Reverse animation
      btn.style.transform = 'scale(0.9)';
      setTimeout(() => {
        btn.classList.remove('product-card-new__wishlist-btn--active');
        svg.style.fill = 'none';
        svg.style.stroke = 'currentColor';
        btn.style.transform = 'scale(1)';
        setTimeout(() => {
          btn.classList.remove('wishlist-btn--animating');
        }, 200);
      }, 150);
    }
  }

  // ============================================
  // GET PRODUCT DATA FROM CARD
  // ============================================

  function getProductDataFromCard(card) {
    const productId = card.dataset.productId;
    const handle = card.querySelector('.product-card-new__wishlist-btn')?.dataset.productHandle;

    if (!productId || !handle) return null;

    // Get selected variant (from color/size selection)
    // Priority: 
    // 1. Data attribute on card (locked variant)
    // 2. Add to cart button
    // 3. First available variant

    let variantId = null;

    // Check if card has a "locked" variant (from color selection)
    const addToCartBtn = card.querySelector('.product-card-new__add-to-cart-btn');
    if (addToCartBtn && addToCartBtn.dataset.variantId) {
      variantId = addToCartBtn.dataset.variantId;
    }

    if (!variantId) {
      const firstVariant = card.querySelector('[data-variant-id]');
      variantId = firstVariant?.dataset.variantId || null;
    }

    // Get selected color
    const activeSwatch = card.querySelector('.product-card-new__swatch--active');
    const color = activeSwatch?.dataset.colorValue || null;

    // Get selected size
    let size = null;
    const sizeModal = card.querySelector('.product-card-new__size-modal');
    if (sizeModal) {
      const selectedSize = sizeModal.querySelector('.product-card-new__size-option-btn--selected');
      size = selectedSize?.dataset.size || null;
    }

    // Get active image
    const activeImage = card.querySelector('.product-card-new__image--active') ||
      card.querySelector('.product-card-new__image');
    const image = activeImage?.src || '';
    // Ensure we get a high-res or decent size image, not a small thumbnail if possible, 
    // but srcset usually handles display. src is fine for reference.

    // Get Price
    const priceEl = card.querySelector('.product-card-new__price .price-item--sale') ||
      card.querySelector('.product-card-new__price');
    const price = priceEl?.textContent?.trim() || '';

    return {
      productId: productId,
      variantId: variantId,
      handle: handle,
      title: card.querySelector('.product-card-new__title a')?.textContent?.trim() || '',
      price: price,
      image: image,
      url: card.querySelector('.product-card-new__title a')?.href || '',
      color: color,
      size: size
    };
  }

  // ============================================
  // INITIALIZE WISHLIST BUTTONS
  // ============================================

  function updateButtonState(btn) {
    const card = btn.closest('.product-card-new');
    if (!card) return;

    const productId = card.dataset.productId;
    if (!productId) return;

    // For the button on the card, we generally show "Active" if *any* variant is in wishlist
    // OR specifically if we want to be strict, only if the *currently selected* variant is in wishlist.
    // Given the UI UX, usually "Product is in wishlist" is enough to show the red heart.
    // But let's check if we can be specific.
    // The logic below checks if ANY variant of the product is in wishlist.

    const inWishlist = isProductInWishlist(productId);
    const svg = btn.querySelector('svg');

    if (inWishlist) {
      btn.classList.add('product-card-new__wishlist-btn--active');
      if (svg) {
        svg.style.fill = '#B90404';
        svg.style.stroke = '#B90404';
      }
    } else {
      btn.classList.remove('product-card-new__wishlist-btn--active');
      if (svg) {
        svg.style.fill = 'none';
        svg.style.stroke = 'currentColor';
      }
    }
  }

  function initWishlistButtons() {
    const wishlistBtns = document.querySelectorAll('.product-card-new__wishlist-btn, .pdp-wishlist-btn');
    wishlistBtns.forEach(updateButtonState);
  }

  // ============================================
  // WISHLIST BUTTON CLICK HANDLER
  // ============================================

  function handleWishlistButtonClick(e) {
    const btn = e.target.closest('.product-card-new__wishlist-btn, .pdp-wishlist-btn');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const card = btn.closest('.product-card-new');
    let productData = null;

    if (card) {
      productData = getProductDataFromCard(card);
    }

    // Fallback: If card data extraction failed (or card not found), try button dataset directly
    if (!productData && btn.dataset.productId && btn.dataset.productHandle) {
      productData = {
        productId: btn.dataset.productId,
        handle: btn.dataset.productHandle,
        variantId: null, // Default
        title: '', // Optional
        price: '', // Optional
        image: '', // Optional
        url: '',   // Optional
        color: null,
        size: null
      };

      // Try to scrape title/image relative to button if possible
      if (card) {
        const titleEl = card.querySelector('.product-card-new__title a');
        if (titleEl) {
          productData.title = titleEl.textContent.trim();
          productData.url = titleEl.href;
        }
        const imgEl = card.querySelector('.product-card-new__image');
        if (imgEl) productData.image = imgEl.src;
      }
    } else if (!card && btn.dataset.productId && btn.dataset.productHandle) {
      // Original PDP fallback (no card container) is covered above, but let's keep specific PDP scraping if needed
      // Actually the block above covers it generically.
      // But for PDP main button, we might want document-level scraping.
      if (!productData) {
        productData = {
          productId: btn.dataset.productId,
          handle: btn.dataset.productHandle,
          variantId: document.querySelector('input[name="id"]')?.value || null,
          title: document.querySelector('.product-title')?.innerText || '',
          price: document.querySelector('.price-item--sale, .price-item--regular')?.innerText || '',
          image: document.querySelector('.main-media-image')?.src || '',
          url: window.location.href
        };
      }
    }

    if (!productData) {
      console.warn('Could not extract product data');
      return;
    }

    // Determine action based on current state of the Product (not just the variant)
    // If the button is active, it means *something* of this product is in wishlist.
    // If we click it, do we remove *that specific variant* or *everything*?
    // UX Standard: 
    // - If active, and we are viewing a specific variant that IS in wishlist -> Remove that variant.
    // - If active, but current variant IS NOT in wishlist -> Add current variant? Or Remove all?
    // Let's go with: Toggle current variant state. 
    // WAIT. The prompt says "Wishlist button stays red once added".
    // Simple approach:
    // Check if THIS specific variant/configuration is in wishlist.
    // Actually, `initWishlistButtons` checks `isProductInWishlist`. 

    // Let's refine for "Sticky" feel:
    // If card shows red, it means "Added".
    // Clicking it again should probably remove it.

    // However, since we track variants (Color/Size), we should try to be precise if possible.
    // But `isProductInWishlist` is broad.

    // Implementation for "Issue A":
    // "Clicking... turns red briefly... then immediately reverts" -> broken state persistence.
    // We fixed the persistence functions above.

    // Let's use the UI state to decide broad intent, but use data for action.

    const isCurrentlyActive = btn.classList.contains('product-card-new__wishlist-btn--active');

    if (isCurrentlyActive) {
      // It's active. Remove.
      removeFromWishlist(productData.productId);
      // Always animate to inactive if user clicked "active" button
      animateWishlistButton(btn, false);
    } else {
      // Add to wishlist
      addToWishlist(productData);
      // Always animate to active if user clicked "inactive" button
      animateWishlistButton(btn, true);
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function initWishlistSystem() {
    // Initialize button states
    initWishlistButtons();

    // Add click handlers - Use a single global listener for efficiency and delegation
    // We verify if listener is already attached to avoid duplicates if this runs multiple times?
    // actually `document.addEventListener` handles duplicate identical listeners by ignoring them.
    document.removeEventListener('click', handleWishlistButtonClick); // Safety cleanup
    document.addEventListener('click', handleWishlistButtonClick);

    // Update buttons when wishlist changes (from anywhere)
    window.removeEventListener(EVENT_NAME, initWishlistButtons);
    window.addEventListener(EVENT_NAME, function () {
      initWishlistButtons();
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistSystem);
  } else {
    initWishlistSystem();
  }

  // Re-initialize for dynamically loaded cards
  document.addEventListener('shopify:section:load', initWishlistSystem);
  document.addEventListener('shopify:section:reorder', initWishlistSystem);

  // Watch for dynamically injected product cards (PDP carousels, AJAX loads)
  const cardObserver = new MutationObserver(function (mutations) {
    let shouldUpdate = false;
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) {
          // Check if node is a product card or carousel slide
          if (node.classList && (
            node.classList.contains('product-card-new') ||
            node.classList.contains('ezs-rec-card') ||
            node.classList.contains('complementary-slide') ||
            node.classList.contains('slider__slide')
          )) {
            shouldUpdate = true;
          }
          // Check if node contains a product card or wishlist button
          else if (node.querySelector && (
            node.querySelector('.product-card-new') ||
            node.querySelector('.product-card-new__wishlist-btn') ||
            node.querySelector('.ezs-rec-card') ||
            node.querySelector('.complementary-slide') ||
            node.querySelector('.slider__slide')
          )) {
            shouldUpdate = true;
          }
        }
      });
    });
    if (shouldUpdate) {
      // Use setTimeout to ensure DOM is fully settled
      setTimeout(function () {
        initWishlistButtons();
      }, 50);
    }
  });

  // Start observing the document body for new product cards
  if (document.body) {
    cardObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Also observe carousel containers specifically
  const carouselObserver = new MutationObserver(function (mutations) {
    let shouldUpdate = false;
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) {
          if (node.classList && (
            node.classList.contains('ezs-rec-card') ||
            node.classList.contains('ezs-rec-track') ||
            node.classList.contains('complementary-slide') ||
            node.classList.contains('slider__slide')
          )) {
            shouldUpdate = true;
          } else if (node.querySelector && (
            node.querySelector('.ezs-rec-card') ||
            node.querySelector('.product-card-new') ||
            node.querySelector('.product-card-new__wishlist-btn') ||
            node.querySelector('.complementary-slide') ||
            node.querySelector('.slider__slide')
          )) {
            shouldUpdate = true;
          }
        }
      });
    });
    if (shouldUpdate) {
      setTimeout(function () {
        initWishlistButtons();
      }, 100);
    }
  });

  // Observe all carousel tracks and sliders
  function observeCarousels() {
    const selectors = [
      '.ezs-rec-track',
      '.ezs-rec-slider-container',
      '.complementary-slider',
      '.slider',
      '.slider__slide',
      '[class*="slider"]',
      '[class*="carousel"]'
    ];

    selectors.forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (container) {
        carouselObserver.observe(container, {
          childList: true,
          subtree: true
        });
      });
    });
  }

  // Initial observation
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCarousels);
  } else {
    observeCarousels();
  }

  // Re-observe when new sections are added
  const sectionObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1 && node.querySelector) {
          const carousels = node.querySelectorAll('.ezs-rec-track, .ezs-rec-slider-container, .complementary-slider, .slider, [class*="slider"], [class*="carousel"]');
          if (carousels.length > 0) {
            carousels.forEach(function (carousel) {
              carouselObserver.observe(carousel, {
                childList: true,
                subtree: true
              });
            });
            setTimeout(function () {
              initWishlistButtons();
            }, 100);
          }

          // Also check for product cards directly
          const productCards = node.querySelectorAll('.product-card-new, .product-card-new__wishlist-btn');
          if (productCards.length > 0) {
            setTimeout(function () {
              initWishlistButtons();
            }, 100);
          }
        }
      });
    });
  });

  if (document.body) {
    sectionObserver.observe(document.body, {
      childList: true,
      subtree: false
    });
  }

  // Export functions globally
  window.IlltochillWishlist = {
    getWishlist,
    addToWishlist,
    removeFromWishlist,
    removeWishlistItemByKey,
    isInWishlist,
    isProductInWishlist,
    init: initWishlistSystem
  };
})();
